import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, DialogHeader, MaterialUICore } from '@iclinic/design-system';
import { Form, useFormik, FormikProvider } from 'formik';
import Turnstile from 'react-turnstile';

import {
  triggerSubscription,
  updateCurrentSubscriptionStep,
  updateSelectedPhysicians,
  updateSubscriptionStepMapper,
} from '../../state/reducer';
import {
  getCurrentSubscriptionStep,
  getRecurrence,
  isSubscribing,
} from '../../state/selectors';
import { StepTypes, SubscriptionFormData } from '../../state/types';
import { PaymentSummary } from '../PaymentSummary';
import { validationForm } from './validations';
import { initialFormData } from './form.normalizer';
import PaymentForm from './PaymentForm';
import ErrorsMessage from './ErrorsMessage';
import * as S from './ModalPaymentForm.styles';
import { VOUCHER_STORAGE_KEY } from 'features/new-auth/stepSignup/constants';

const { useMediaQuery, useTheme } = MaterialUICore;

const ModalPaymentForm = () => {
  const dispatch = useDispatch();
  const isAnnual = useSelector(getRecurrence);
  const currentStep = useSelector(getCurrentSubscriptionStep);
  const isLoading = useSelector(isSubscribing);

  const onSubmit = (formData: SubscriptionFormData) => {
    dispatch(triggerSubscription(formData));
    localStorage.removeItem(VOUCHER_STORAGE_KEY);
  };

  const formik = useFormik({
    initialValues: initialFormData,
    validationSchema: validationForm,
    onSubmit,
  });

  const handleClose = () => {
    dispatch(updateSelectedPhysicians());
    dispatch(updateSubscriptionStepMapper());
    dispatch(updateCurrentSubscriptionStep());
    formik.resetForm();
  };

  const goBackToAddress = () => {
    dispatch(updateCurrentSubscriptionStep(StepTypes.ADDRESS));
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isOpen = currentStep === StepTypes.PAYMENT;

  return (
    <FormikProvider value={formik}>
      <Form>
        <S.PaymentModal
          open={isOpen}
          maxWidth="xl"
          onClose={handleClose}
          fullScreen={isMobile}
          scroll="paper"
        >
          <DialogHeader title="Dados de pagamento" onClose={handleClose} />

          <S.DialogContent>
            <ErrorsMessage />

            <PaymentForm />

            <S.SummaryWrapper>
              <PaymentSummary isAnnual={isAnnual} />
            </S.SummaryWrapper>

            <S.WrapperTurnstile>
              <Turnstile
                sitekey={process.env.CF_TURNSTILE_SITE_KEY || ''}
                onVerify={(token) =>
                  formik.setFieldValue('turnstileToken', token)
                }
                retryInterval={300}
                theme="light"
                refreshExpired="auto"
              />
            </S.WrapperTurnstile>

            <S.DialogActions>
              <Button
                disabled={isLoading}
                color="transparent"
                onClick={goBackToAddress}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                disabled={isLoading}
                isLoading={isLoading}
                onClick={() => formik.handleSubmit()}
              >
                Completar assinatura
              </Button>
            </S.DialogActions>
          </S.DialogContent>
        </S.PaymentModal>
      </Form>
    </FormikProvider>
  );
};

export default ModalPaymentForm;
